/**
 * TabletShell — layout pour `768-1279px` : Sidebar + 1 drawer latéral + center.
 *
 * @remarks
 * Pas de 3 panneaux simultanés (trop étroit) : un seul tiroir à la fois, le
 * centre reste toujours visible.
 *
 * Les tiroirs suivent le layout store, comme sur desktop : les boutons panneau
 * du `Header`, Ctrl+B / Ctrl+Shift+B et les features (copilot, admin) agissent
 * donc aussi ici. Avant, la tablette tenait son propre état et ajoutait une
 * barre « Outils / Détails » qui doublait les boutons du header, eux restés
 * sans effet sur ce shell.
 */

import { Suspense, useCallback, useMemo } from 'react';
import { useCommand } from '@/app/commands/command-bus';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { useFeatureFlagsStore } from '@/app/stores/use-feature-flags-store';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { useCurrentContext } from '@/app/router/use-current-context';
import { useContextLifecycle } from '@/app/router/use-context-lifecycle';
import { useFeatureStoresSubscription } from '@/app/router/use-feature-stores-subscription';
import { useFeatureShortcuts } from '@/app/router/use-feature-shortcuts';
import { useFocusOnRouteChange } from '@/app/router/use-focus-on-route-change';
import { useDocumentTitle } from '@/app/router/use-document-title';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Panel } from '@/app/panels';
import { SkipLink } from './SkipLink';
import { ActiveGlobalLoader } from './global-loader-variants';
import { GlobalLoaderZone } from './GlobalLoaderZone';
import { featureRegistry, type AppContext } from '@/app/registry';
import { useExclusiveDrawers } from './use-exclusive-drawers';

export function TabletShell() {
  const context = useCurrentContext();
  const user = useAuthStore((s) => s.user);
  const flags = useFeatureFlagsStore((s) => s.flags);
  const appCtx = useMemo<AppContext>(() => ({ user, flags }), [user, flags]);

  useFeatureStoresSubscription();
  useFeatureShortcuts(context, appCtx);
  useContextLifecycle(context, appCtx);
  useFocusOnRouteChange(context);
  useDocumentTitle(context, appCtx);

  const leftActiveTab = useLayoutStore((s) => s.leftActiveTab);
  const rightActiveTab = useLayoutStore((s) => s.rightActiveTab);
  const setLeftActiveTab = useLayoutStore((s) => s.setLeftActiveTab);
  const setRightActiveTab = useLayoutStore((s) => s.setRightActiveTab);
  const leftDrawerOpen = useLayoutStore((s) => s.leftDrawerOpen);
  const rightDrawerOpen = useLayoutStore((s) => s.rightDrawerOpen);
  const toggleLeftDrawer = useLayoutStore((s) => s.toggleLeftDrawer);
  const toggleRightDrawer = useLayoutStore((s) => s.toggleRightDrawer);

  const leftTabs = featureRegistry.getLeftPanelTabs(context, appCtx);
  const rightTabs = featureRegistry.getRightPanelTabs(context, appCtx);
  const CenterView = featureRegistry.getCenterView(context, appCtx);

  const showLeft = leftDrawerOpen && leftTabs.length > 0;
  const showRight = rightDrawerOpen && rightTabs.length > 0;

  useExclusiveDrawers(showLeft, showRight);

  // Raccourcis Ctrl+B / Ctrl+Shift+B (`shell-manifest`) : mêmes actions que les
  // boutons du header.
  const handleTogglePanel = useCallback(
    (side: 'left' | 'right') => {
      if (side === 'left') toggleLeftDrawer();
      else toggleRightDrawer();
    },
    [toggleLeftDrawer, toggleRightDrawer],
  );
  useCommand('shell:toggle-panel', handleTogglePanel);

  return (
    <div className="bg-bg text-fg flex h-screen w-screen flex-col overflow-hidden">
      <SkipLink />
      <Header context={context} appCtx={appCtx} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar context={context} appCtx={appCtx} />
        <div className="flex flex-1 overflow-hidden">
          {showLeft && (
            <div className="border-border w-72 border-r">
              <Panel
                tabs={leftTabs}
                activeTabId={leftActiveTab}
                onTabClick={setLeftActiveTab}
                side="left"
              />
            </div>
          )}

          <main id="main-content" tabIndex={-1} className="flex flex-1 flex-col overflow-auto">
            <GlobalLoaderZone>
              <div className="relative flex-1 overflow-auto">
                {CenterView ? (
                  <Suspense fallback={null}>
                    <CenterView />
                  </Suspense>
                ) : (
                  <div className="text-fg-muted flex h-full items-center justify-center px-6 text-center text-sm">
                    Pas de vue pour <code className="ml-1">{context}</code>
                  </div>
                )}
                <ActiveGlobalLoader />
              </div>
            </GlobalLoaderZone>
          </main>

          {showRight && (
            <div className="border-border w-72 border-l">
              <Panel
                tabs={rightTabs}
                activeTabId={rightActiveTab}
                onTabClick={setRightActiveTab}
                side="right"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
